/**
 * PostCloseQueueView — the Post-Close lane of the Lock Queue.
 *
 * Data source: `GET /pricing/post_close/all_locks` (capital-markets only) — every
 * post-close lock doc, the post-close peer of `/pricing/all_locks`. Filtered +
 * searched client-side, same as the pre-close queue. Both tables use AG Grid
 * master/detail: a row expands inline to the shared PostCloseLockPanel (view /
 * request / confirm / cancel + live price preview).
 *
 * Loans with NO lock yet aren't in that feed, so "New lock" swaps the content
 * for a Salesforce search (`/pricing/post_close/search`) rendered as an
 * expandable table (return button top-left); expanding a result opens the same
 * panel to start a lock.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry } from "ag-grid-community";
import { MasterDetailModule } from "ag-grid-enterprise";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import AddRounded from "@mui/icons-material/AddRounded";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import RefreshRounded from "@mui/icons-material/RefreshRounded";

import AgGridTableWrapper from "../../components/agGrid/AgGridTableWrapper";
import {
  PillGroup,
  QueueScopeRow,
  QueueStatusBar,
} from "../../components/queueToolbar/queueToolbar";
import SearchInput from "../../components/inputs/SearchInput";
import { T } from "../SupportScreen/components/supportUi";
import { formatDate } from "../../_src_shared/utils/functions/formatDate";
import formatDollar from "../../_src_shared/utils/functions/formatDollar";
import { formatPercent } from "../../utils/numbers/formatPercent";
import {
  noteRateToDecimal,
  postCloseLockApi,
  postCloseProductForProgram,
} from "../../api/postCloseLockApi";
import PostCloseLockPanel from "../LoanDrilldownScreen/tabs/LoanDrilldownLoanLockTab/PostCloseLockPanel";

// Master/detail (expandable rows) is an enterprise feature — register it (the
// parent screen does too; registration is idempotent).
ModuleRegistry.registerModules([MasterDetailModule]);

const MS_DAY = 86400000;
const ALL = "__all__";

const STATUS_META = {
  Requested: { bg: "#fef3e2", fg: T.amber },
  Confirmed: { bg: "#effaf2", fg: T.green },
  Repriced: { bg: "#eff4ff", fg: T.blue },
  Cancelled: { bg: "#f1f4f6", fg: T.slate },
  Expired: { bg: "#fdecec", fg: T.red },
  Superseded: { bg: "#f1f4f6", fg: T.slate },
};
const PRODUCT_META = {
  dscr: { label: "DSCR", bg: "#eef2ff", fg: "#4f46e5" },
  rtl: { label: "RTL", bg: T.teal50, fg: T.teal600 },
};
const STAGE_META = {
  Lead: { bg: "#f1f4f6", fg: T.slate },
  Prospect: { bg: "#f1f4f6", fg: T.slate },
  Processing: { bg: "#eff4ff", fg: T.blue },
  Underwriting: { bg: "#eff4ff", fg: T.blue },
  "Condition Clearing": { bg: "#fef3e2", fg: T.amber },
  "Condition Clearance": { bg: "#fef3e2", fg: T.amber },
  "Final Review": { bg: "#effaf2", fg: T.green },
  "Clear To Close": { bg: "#effaf2", fg: T.green },
  Closed: { bg: "#eef2ff", fg: "#4f46e5" },
};
const STATUS_FILTERS = [
  { key: ALL, label: "All" },
  { key: "Requested", label: "Requested" },
  { key: "Confirmed", label: "Confirmed" },
  { key: "Expired", label: "Expired" },
  { key: "Cancelled", label: "Cancelled" },
];

const Tag = ({ bg, fg, children }) => (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      fontSize: "11.5px",
      fontWeight: 700,
      letterSpacing: ".02em",
      lineHeight: 1.5,
      color: fg,
      background: bg,
      borderRadius: "6px",
      px: "8px",
      py: "3px",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </Box>
);
const Dash = () => (
  <Box component="span" sx={{ color: T.ink300 }}>
    —
  </Box>
);
const StageChip = ({ stage }) => {
  if (!stage) return null;
  const m = STAGE_META[stage] || { bg: "#f1f4f6", fg: T.slate };
  return (
    <Tag bg={m.bg} fg={m.fg}>
      {stage}
    </Tag>
  );
};

const lockProductOf = (lock) =>
  (lock?.product || "").toLowerCase() ||
  (lock?.coupon_rate != null ? "rtl" : "dscr");
const lockRateOf = (lock) => {
  const rtl = lockProductOf(lock) === "rtl";
  return rtl ? (lock?.coupon_rate ?? lock?.lock_buy_rate) : lock?.rate_at_lock;
};
const expDaysOf = (lock) => {
  const raw = lock?.lock_expiration_date;
  if (!raw) return null;
  const t = new Date(raw).getTime();
  if (isNaN(t)) return null;
  return Math.ceil((t - Date.now()) / MS_DAY);
};
const lockKey = (l) =>
  l?._id?.$oid || l?._id || `${l?.opportunityId}-${l?.lock_status}`;

// Split the flat `address` ("25 Ripley Rd, Cohasset, MA, 02025") into a street +
// "City, ST ZIP" using the structured city/state/zip fields.
const splitAddress = (opp) => {
  const { address = "", city, state, zip } = opp || {};
  const cityStateZip = [city, [state, zip].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");
  let street = address;
  if (city && address.includes(city)) {
    street = address.slice(0, address.indexOf(city)).replace(/[,\s]+$/, "");
  }
  return {
    street: street || address || "—",
    cityStateZip: cityStateZip || null,
  };
};

// Synthetic loan shapes the PostCloseLockPanel reads (resolves the opp id off
// `salesforceOpportunityId`).
const loanFromLock = (lock) => ({
  salesforceOpportunityId: lock?.opportunityId,
  loanNumber: lock?.loanId || lock?.loanNumber,
  loanProductType: lock?.product,
});
const loanFromOpp = (opp) => ({
  salesforceOpportunityId: opp?.opportunityId,
  loanNumber: opp?.loanId,
  loanProductType: opp?.loanProgram,
  loanStatus: opp?.stage,
});

// ── inline expandable detail (the lock panel) ──
// AG Grid detailCellRenderer. `source` ("queue" | "search") picks the synthetic
// loan shape; the queue passes its lock list so we can show this opp's history.
const PostCloseDetail = (params) => {
  const { data, source, onChanged, locks } = params;
  const loan = source === "search" ? loanFromOpp(data) : loanFromLock(data);
  const history =
    source === "queue"
      ? (locks || []).filter((l) => l.opportunityId === data?.opportunityId)
      : undefined;
  return (
    <Box sx={{ background: "#fbfcfd", borderTop: `1px solid ${T.line}` }}>
      <PostCloseLockPanel loan={loan} history={history} onChanged={onChanged} />
    </Box>
  );
};

// Two-line group cell (the expand chevron is supplied by agGroupCellRenderer).
const QueueLoanCell = (p) => (
  <Box sx={{ lineHeight: 1.3, py: 0.5, minWidth: 0 }}>
    <Box
      sx={{
        fontWeight: 600,
        color: T.ink900,
        fontSize: 13,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {p.data?.loanName || p.data?.loanId || `#${p.data?.loanNumber}` || "—"}
    </Box>
    {p.data?.lock_request_user?.fullName && (
      <Box sx={{ fontSize: 11.5, color: T.ink500 }}>
        Requested by {p.data.lock_request_user.fullName}
      </Box>
    )}
  </Box>
);
const SearchLoanCell = (p) => {
  const { street, cityStateZip } = splitAddress(p.data);
  return (
    <Box sx={{ lineHeight: 1.3, py: 0.5, minWidth: 0 }}>
      <Box
        sx={{
          fontWeight: 700,
          color: T.ink900,
          fontSize: 13,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {p.data?.loanId || `#${p.data?.loanNumber}`}
      </Box>
      <Box
        sx={{
          fontSize: 11.5,
          color: T.ink500,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {[street, cityStateZip].filter(Boolean).join(" · ")}
      </Box>
    </Box>
  );
};

// Shared wrapper sx so the group cell (chevron + content) centers vertically.
const GRID_WRAP_SX = {
  "& .ag-cell-wrapper": { alignItems: "center", minWidth: 0 },
  "& .ag-cell-wrapper > .ag-group-value": {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
  },
};

// ── mode switch (Pre-Close ↔ Post-Close), the top row of both lock views ──
const MODE_PILLS = [
  { key: "preclose", label: "Pre-Close" },
  { key: "postclose", label: "Post-Close" },
];
export const ModeTabs = ({ mode, setMode }) => (
  <PillGroup options={MODE_PILLS} value={mode} onChange={setMode} />
);

const HeaderBtn = ({ children, onClick, loading, primary }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    disabled={loading}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      font: "inherit",
      fontSize: 13.5,
      flexShrink: 0,
      fontWeight: 600,
      color: primary ? "#fff" : T.ink700,
      background: primary ? T.teal600 : "#fff",
      border: `1px solid ${primary ? T.teal600 : T.line}`,
      borderRadius: "8px",
      boxShadow: T.shadow,
      px: "14px",
      py: "9px",
      cursor: loading ? "default" : "pointer",
      opacity: loading ? 0.6 : 1,
      "&:hover": {
        filter: primary ? "brightness(0.95)" : "none",
        borderColor: primary ? T.teal600 : T.ink300,
      },
    }}
  >
    {children}
  </Box>
);

const ReturnButton = ({ onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      font: "inherit",
      fontSize: 13.5,
      fontWeight: 600,
      color: T.teal600,
      background: "#fff",
      border: `1px solid ${T.line}`,
      borderRadius: "8px",
      boxShadow: T.shadow,
      px: "12px",
      py: "8px",
      cursor: "pointer",
      "&:hover": { borderColor: T.ink300 },
    }}
  >
    <ArrowBackRounded sx={{ fontSize: 17 }} /> Back to post-close queue
  </Box>
);

const TableCard = ({ children }) => (
  <Box
    sx={{
      background: T.card,
      border: `1px solid ${T.line}`,
      borderRadius: T.radius,
      boxShadow: T.shadow,
      overflow: "hidden",
    }}
  >
    {children}
  </Box>
);

const EmptyState = ({ title, body }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 1,
      py: 8,
      textAlign: "center",
    }}
  >
    <Box sx={{ fontSize: 15, fontWeight: 700, color: T.ink900 }}>{title}</Box>
    <Box sx={{ fontSize: 13, color: T.ink500, maxWidth: 400 }}>{body}</Box>
  </Box>
);

// ── search-to-start-a-new-lock view (replaces the queue content) ──
const SearchView = ({ onReturn, onChanged }) => {
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDq(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    postCloseLockApi
      .search(dq)
      .then(
        (res) =>
          live && setRows(Array.isArray(res?.results) ? res.results : []),
      )
      .catch(
        (e) =>
          live &&
          setError(e?.response?.data?.detail || e?.message || "Search failed."),
      )
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [dq]);

  const detailParams = useMemo(
    () => ({ source: "search", onChanged }),
    [onChanged],
  );
  const columnDefs = useMemo(
    () => [
      {
        headerName: "Loan",
        field: "loanId",
        flex: 2,
        minWidth: 240,
        cellRenderer: "agGroupCellRenderer",
        cellRendererParams: { innerRenderer: SearchLoanCell },
        cellStyle: { display: "flex", alignItems: "center", height: "100%" },
      },
      {
        headerName: "Product",
        minWidth: 100,
        valueGetter: (p) => postCloseProductForProgram(p.data?.loanProgram),
        cellRenderer: (p) => {
          const m = PRODUCT_META[p.value];
          return m ? (
            <Tag bg={m.bg} fg={m.fg}>
              {m.label}
            </Tag>
          ) : (
            <Dash />
          );
        },
      },
      {
        headerName: "Stage",
        field: "stage",
        minWidth: 150,
        cellRenderer: (p) => <StageChip stage={p.value} />,
      },
      { headerName: "Purpose", field: "loanPurpose", minWidth: 150 },
      {
        headerName: "Amount",
        minWidth: 120,
        valueGetter: (p) => p.data?.finalLoanAmount ?? null,
        valueFormatter: (p) => (p.value == null ? "—" : formatDollar(p.value)),
      },
      {
        headerName: "Note rate",
        minWidth: 110,
        valueGetter: (p) => p.data?.noteRate ?? null,
        valueFormatter: (p) =>
          p.value == null ? "—" : formatPercent(noteRateToDecimal(p.value)),
      },
    ],
    [],
  );

  return (
    <Box sx={{ background: T.bg, flex: "1 0 auto", minHeight: "100%" }}>
      <Container maxWidth="xl" sx={{ pt: "28px", pb: "72px" }}>
        <ReturnButton onClick={onReturn} />
        <Box sx={{ mt: 2.5 }}>
          <Box sx={{ fontSize: 22, fontWeight: 700, color: T.ink900 }}>
            Start a post-close lock
          </Box>
          <Box sx={{ color: T.ink500, mt: "5px", fontSize: 13.5 }}>
            Search the Salesforce Post-close population, then expand a loan to
            request its lock.
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            m: "20px 0 14px",
          }}
        >
          <Box sx={{ flex: "0 1 420px", minWidth: 240 }}>
            <SearchInput
              value={q}
              onChange={(v) => setQ(v)}
              placeholder="Search loan Id, name, address…"
              variant="paper"
              size="small"
              fullWidth
              autoFocus
            />
          </Box>
          {!loading && !error && (
            <Box sx={{ fontSize: 12.5, color: T.ink500 }}>
              {rows.length} loan{rows.length === 1 ? "" : "s"}
              {q.trim() ? "" : " · most recent"}
            </Box>
          )}
        </Box>

        <TableCard>
          {error ? (
            <Box sx={{ p: 4, color: T.red, fontSize: 13.5 }}>{error}</Box>
          ) : loading ? (
            <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
              <CircularProgress size={26} />
            </Box>
          ) : rows.length === 0 ? (
            <EmptyState
              title="No post-close loans found"
              body="Try a different loan Id, name, or address. An empty search returns the most recent loans in the population."
            />
          ) : (
            <AgGridTableWrapper
              height="auto"
              noBorder
              noBorderRadius
              sx={GRID_WRAP_SX}
            >
              <AgGridReact
                rowData={rows}
                columnDefs={columnDefs}
                defaultColDef={{
                  resizable: true,
                  sortable: true,
                  suppressHeaderMenuButton: true,
                  minWidth: 90,
                  cellStyle: { display: "flex", alignItems: "center" },
                }}
                getRowId={(p) => p.data?.opportunityId}
                domLayout="autoHeight"
                pagination
                paginationPageSize={25}
                rowHeight={56}
                headerHeight={40}
                suppressCellFocus
                masterDetail
                detailCellRenderer={PostCloseDetail}
                detailCellRendererParams={detailParams}
                detailRowAutoHeight
                keepDetailRows
                rowStyle={{ cursor: "pointer" }}
                onRowClicked={(e) => e.node.setExpanded(!e.node.expanded)}
              />
            </AgGridTableWrapper>
          )}
        </TableCard>
      </Container>
    </Box>
  );
};

const PostCloseQueueView = ({ mode, setMode }) => {
  const [locks, setLocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [noListAccess, setNoListAccess] = useState(false);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(ALL);
  const [searchMode, setSearchMode] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNoListAccess(false);
    try {
      const rows = await postCloseLockApi.allLocks();
      setLocks(Array.isArray(rows) ? rows : []);
    } catch (e) {
      if (e?.response?.status === 403) setNoListAccess(true);
      else
        setError(
          e?.response?.data?.detail ||
            e?.message ||
            "Failed to load post-close locks.",
        );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const statusCounts = useMemo(() => {
    const counts = { [ALL]: locks.length };
    STATUS_FILTERS.forEach(({ key }) => {
      if (key === ALL) return;
      counts[key] = locks.filter((l) => l.lock_status === key).length;
    });
    return counts;
  }, [locks]);

  const stats = useMemo(() => {
    const requested = locks.filter((l) => l.lock_status === "Requested").length;
    const confirmed = locks.filter(
      (l) =>
        l.lock_status === "Confirmed" &&
        (expDaysOf(l) == null || expDaysOf(l) >= 0),
    ).length;
    const expiringSoon = locks.filter((l) => {
      const d = expDaysOf(l);
      return l.lock_status === "Confirmed" && d != null && d >= 0 && d <= 7;
    }).length;
    return { requested, confirmed, expiringSoon };
  }, [locks]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return locks.filter((l) => {
      if (statusFilter !== ALL && l.lock_status !== statusFilter) return false;
      if (!q) return true;
      const hay =
        `${l.loanName || ""} ${l.loanId || ""} ${l.loanNumber || ""} ${
          l.lock_counterparty || ""
        } ${l.lock_request_user?.fullName || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [locks, search, statusFilter]);

  const detailParams = useMemo(
    () => ({ source: "queue", onChanged: load, locks }),
    [load, locks],
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Loan",
        field: "loanName",
        flex: 2.2,
        minWidth: 240,
        cellRenderer: "agGroupCellRenderer",
        cellRendererParams: { innerRenderer: QueueLoanCell },
        cellStyle: { display: "flex", alignItems: "center", height: "100%" },
      },
      {
        headerName: "Product",
        minWidth: 100,
        valueGetter: (p) => lockProductOf(p.data),
        cellRenderer: (p) => {
          const m = PRODUCT_META[p.value];
          return m ? (
            <Tag bg={m.bg} fg={m.fg}>
              {m.label}
            </Tag>
          ) : (
            <Dash />
          );
        },
      },
      {
        headerName: "Status",
        field: "lock_status",
        minWidth: 120,
        cellRenderer: (p) => {
          const m = STATUS_META[p.value];
          return m ? (
            <Tag bg={m.bg} fg={m.fg}>
              {p.value}
            </Tag>
          ) : (
            <Dash />
          );
        },
      },
      {
        headerName: "Rate",
        minWidth: 100,
        valueGetter: (p) => lockRateOf(p.data),
        valueFormatter: (p) => (p.value == null ? "—" : formatPercent(p.value)),
      },
      {
        headerName: "Amount",
        minWidth: 120,
        valueGetter: (p) =>
          p.data?.loan_terms_at_lock?.finalLoanAmount ??
          p.data?.loan_terms_at_lock?.initialLoanAmount ??
          null,
        valueFormatter: (p) => (p.value == null ? "—" : formatDollar(p.value)),
      },
      {
        headerName: "Expires",
        minWidth: 130,
        valueGetter: (p) => {
          const t = p.data?.lock_expiration_date
            ? new Date(p.data.lock_expiration_date).getTime()
            : null;
          return t && !isNaN(t) ? t : Number.MAX_SAFE_INTEGER;
        },
        cellRenderer: (p) => {
          const d = expDaysOf(p.data);
          if (d == null) return <Dash />;
          if (d < 0)
            return <Box sx={{ color: T.red, fontWeight: 600 }}>Expired</Box>;
          if (d <= 7)
            return (
              <Box sx={{ color: T.amber, fontWeight: 600 }}>{d}d left</Box>
            );
          return (
            <Box sx={{ color: T.ink500 }}>
              {formatDate(p.data?.lock_expiration_date)}
            </Box>
          );
        },
      },
    ],
    [],
  );

  // ── search takes over the whole content area ──
  if (searchMode) {
    return (
      <SearchView onReturn={() => setSearchMode(false)} onChanged={load} />
    );
  }

  return (
    <Box sx={{ background: T.bg, flex: "1 0 auto", minHeight: "100%" }}>
      <Container maxWidth="xl" sx={{ pt: "36px", pb: "72px" }}>
        {/* header */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
          <Box>
            <Box
              sx={{
                fontSize: 23,
                fontWeight: 700,
                letterSpacing: "-.01em",
                color: T.ink900,
              }}
            >
              Lock Queue
            </Box>
            <Box sx={{ color: T.ink500, mt: "5px", fontSize: 13.5 }}>
              Post-close locks are Salesforce-direct over the EV_Post_Close
              population.
            </Box>
          </Box>
          <Box
            sx={{
              ml: "auto",
              display: "flex",
              gap: 1.25,
              alignItems: "center",
            }}
          >
            <HeaderBtn onClick={load} loading={loading}>
              <RefreshRounded sx={{ fontSize: 18 }} /> Refresh
            </HeaderBtn>
            <HeaderBtn primary onClick={() => setSearchMode(true)}>
              <AddRounded sx={{ fontSize: 18 }} /> Start Salesforce Loan Lock
            </HeaderBtn>
          </Box>
        </Box>

        {/* Top — pipeline */}
        <QueueScopeRow label="Pipeline">
          <ModeTabs mode={mode} setMode={setMode} />
        </QueueScopeRow>

        {/* Middle — widgets */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(3,1fr)" },
            gap: 2,
            m: "18px 0",
          }}
        >
          {[
            ["Requested", stats.requested, T.amber],
            ["Active (confirmed)", stats.confirmed, T.green],
            ["Expiring ≤ 7 days", stats.expiringSoon, T.red],
          ].map(([label, n, color]) => (
            <Box
              key={label}
              sx={{
                background: T.card,
                border: `1px solid ${T.line}`,
                borderRadius: T.radius,
                boxShadow: T.shadow,
                p: "16px 18px",
                display: "flex",
                alignItems: "center",
                gap: "14px",
              }}
            >
              <Box
                sx={{
                  width: 4,
                  alignSelf: "stretch",
                  borderRadius: "4px",
                  background: color,
                }}
              />
              <Box>
                <Box sx={{ fontSize: 24, fontWeight: 800, color: T.ink900 }}>
                  {n}
                </Box>
                <Box
                  sx={{
                    fontSize: 12,
                    color: T.ink500,
                    fontWeight: 500,
                    mt: "1px",
                  }}
                >
                  {label}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Bottom — status pills + count + search */}
        <QueueStatusBar
          pills={
            <PillGroup
              options={STATUS_FILTERS}
              value={statusFilter}
              onChange={setStatusFilter}
              counts={statusCounts}
            />
          }
          shown={filtered.length}
          total={locks.length}
          search={search}
          onSearch={setSearch}
          placeholder="Search loan, counterparty, requester…"
        />

        {/* table */}
        <TableCard>
          {noListAccess ? (
            <Box sx={{ display: "flex", gap: 1.25, p: 4 }}>
              <InfoOutlined
                sx={{ fontSize: 18, color: T.ink500, flexShrink: 0, mt: "1px" }}
              />
              <Box sx={{ fontSize: 13.5, color: T.ink500, lineHeight: 1.5 }}>
                The full post-close lock list is restricted to the
                capital-markets team. You can still start a lock with{" "}
                <b>Lock a Salesforce loan</b> above.
              </Box>
            </Box>
          ) : error ? (
            <Box sx={{ p: 4, color: T.red, fontSize: 13.5 }}>{error}</Box>
          ) : loading ? (
            <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
              <CircularProgress size={26} />
            </Box>
          ) : filtered.length === 0 ? (
            <EmptyState
              title={
                locks.length === 0
                  ? "No post-close locks yet"
                  : "No locks match this view"
              }
              body={
                locks.length === 0
                  ? "Start one with “Lock a Salesforce loan”, or they'll appear here as they're requested."
                  : "Try a different status or clear the search."
              }
            />
          ) : (
            <AgGridTableWrapper
              height="auto"
              noBorder
              noBorderRadius
              sx={GRID_WRAP_SX}
            >
              <AgGridReact
                rowData={filtered}
                columnDefs={columnDefs}
                defaultColDef={{
                  resizable: true,
                  sortable: true,
                  suppressHeaderMenuButton: true,
                  minWidth: 90,
                  cellStyle: { display: "flex", alignItems: "center" },
                }}
                getRowId={(p) => lockKey(p.data)}
                domLayout="autoHeight"
                pagination
                paginationPageSize={25}
                rowHeight={54}
                headerHeight={40}
                suppressCellFocus
                masterDetail
                detailCellRenderer={PostCloseDetail}
                detailCellRendererParams={detailParams}
                detailRowAutoHeight
                keepDetailRows
                rowStyle={{ cursor: "pointer" }}
                onRowClicked={(e) => e.node.setExpanded(!e.node.expanded)}
              />
            </AgGridTableWrapper>
          )}
        </TableCard>
      </Container>
    </Box>
  );
};

export default PostCloseQueueView;
